import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import ExcelJS from 'exceljs';
import { machineryService } from '../../../services/machineryService';
import { getProjects } from '../../../services/projectsService';
import Toast from '../../../components/Common/Toast';
import MachineryReportsDashboard from './MachineryReportsDashboard';

const REPORT_TYPES = [
  { value: 'dashboard', label: 'Dashboard resumen', key: 'dashboard' },
  { value: 'spend-by-vehicle', label: 'Gasto por vehículo', key: 'spendByVehicle' },
  { value: 'spend-by-machine', label: 'Gasto por maquinaria', key: 'spendByMachine' },
  { value: 'cost-by-event-type-and-concept', label: 'Por tipo de evento: productos/servicios y costo', key: 'costByEventTypeAndConcept' },
  { value: 'spend-by-line', label: 'Gasto por línea', key: 'spendByLine' },
  { value: 'monetary-inconsistencies', label: 'Inconsistencias monetarias', key: 'monetaryInconsistencies' },
];

const formatCurrency = (n) => {
  if (n == null || n === '') return '—';
  return new Intl.NumberFormat('es-PE', { style: 'currency', currency: 'PEN', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
};

const formatNumber = (n, decimals = 2) => {
  if (n == null || n === '') return '—';
  return new Intl.NumberFormat('es-PE', { minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(n);
};

/** Nombre + placa para celdas de reporte (vehículos). */
const formatVehicleNameWithPlate = (row) => {
  if (!row) return '—';
  const name = (row.vehicle_name || '').trim();
  const plate = (row.license_plate || '').trim();
  if (name && plate) return `${name} · ${plate}`;
  return name || plate || '—';
};

/** Origen del reporte de inconsistencias: etiqueta en español desde el API (incl. serie/número en documentos). */
const formatInconsistencySource = (r) => {
  if (!r) return '—';
  const label = String(r.source_label || '').trim();
  if (label && label !== '—') return label;
  return '—';
};

export default function MachineryReportsIndex() {
  const [reportType, setReportType] = useState('dashboard');
  const [dateFrom, setDateFrom] = useState(() => new Date().toISOString().slice(0, 7) + '-01');
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().slice(0, 10));
  const [vehicleId, setVehicleId] = useState('');
  const [machineId, setMachineId] = useState('');
  const [projectId, setProjectId] = useState('');
  const [vehicles, setVehicles] = useState([]);
  const [machines, setMachines] = useState([]);
  const [categories, setCategories] = useState([]);
  const [projects, setProjects] = useState([]);
  const [reportData, setReportData] = useState([]);
  const [maintenanceAlerts, setMaintenanceAlerts] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });
  const [expandedSections, setExpandedSections] = useState({});

  const toggleSection = (key) => {
    setExpandedSections((prev) => ({ ...prev, [key]: prev[key] === false }));
  };
  const isSectionOpen = (key) => expandedSections[key] !== false;

  /** Limpia resultados al cambiar filtros; el reporte solo se carga con «Generar reporte». */
  const invalidateReport = () => {
    setReportData([]);
    setMaintenanceAlerts([]);
    setMeta(null);
    setExpandedSections({});
  };

  useEffect(() => {
    Promise.all([
      machineryService.getVehicles().then((r) => (r.success && r.data ? r.data : [])),
      machineryService.getMachines().then((r) => (r.success && r.data ? r.data : [])),
      machineryService.getCategories({ active_only: true }).then((r) => (r.success && r.data ? r.data : [])),
      getProjects({ active_only: true }).then((r) => (r.success && r.data ? r.data : r.data || [])).catch(() => []),
    ]).then(([v, m, c, p]) => {
      setVehicles(Array.isArray(v) ? v : []);
      setMachines(Array.isArray(m) ? m : []);
      setCategories(Array.isArray(c) ? c : []);
      setProjects(Array.isArray(p) ? p : []);
    });
  }, []);

  const categoriesById = categories.reduce((acc, cat) => {
    acc[String(cat.id)] = cat;
    return acc;
  }, {});

  const vehiclesById = vehicles.reduce((acc, row) => {
    acc[String(row.id)] = row;
    return acc;
  }, {});

  const machinesById = machines.reduce((acc, row) => {
    acc[String(row.id)] = row;
    return acc;
  }, {});

  const lineLabelForAsset = (asset, scope) => {
    const categoryId = asset?.category_id;
    if (!categoryId) return 'Otro';
    const category = categoriesById[String(categoryId)];
    if (!category || category.scope !== scope) return 'Otro';
    return category.name || 'Otro';
  };

  const loadReport = async () => {
    if (!dateFrom || !dateTo) {
      setToast({ isOpen: true, message: 'Indique rango de fechas', type: 'warning' });
      return;
    }
    setLoading(true);
    setReportData([]);
    setMaintenanceAlerts([]);
    setMeta(null);
    const params = { date_from: dateFrom, date_to: dateTo };
    if (reportType === 'spend-by-vehicle' && vehicleId != null && String(vehicleId).trim() !== '') {
      params.vehicle_id = vehicleId;
    }
    if (reportType === 'spend-by-machine' && machineId != null && String(machineId).trim() !== '') {
      params.machine_id = machineId;
    }
    if (reportType === 'cost-by-event-type-and-concept' && vehicleId != null && String(vehicleId).trim() !== '') {
      params.vehicle_id = vehicleId;
    }
    if (projectId != null && String(projectId).trim() !== '') params.project_id = projectId;

    const config = REPORT_TYPES.find((r) => r.value === reportType);
    let res;
    switch (config.key) {
      case 'dashboard': {
        const [dashRes, alertsRes] = await Promise.all([
          machineryService.getReportDashboard(params),
          machineryService.getMaintenanceAlerts(),
        ]);
        res = dashRes;
        if (alertsRes.success && Array.isArray(alertsRes.data)) {
          setMaintenanceAlerts(alertsRes.data);
        }
        break;
      }
      case 'spendByVehicle':
        res = await machineryService.getReportSpendByVehicle(params);
        break;
      case 'spendByMachine':
        res = await machineryService.getReportSpendByMachine(params);
        break;
      case 'costByEventTypeAndConcept':
        res = await machineryService.getReportCostByEventTypeAndConceptGrouped(params);
        break;
      case 'spendByLine':
        res = await machineryService.getReportSpendByLine(params);
        break;
      case 'monetaryInconsistencies':
        res = await machineryService.getReportMonetaryInconsistencies(params);
        break;
      default:
        res = { success: false };
    }

    if (res.success && res.data) {
      const config = REPORT_TYPES.find((r) => r.value === reportType);
      if (config?.key === 'dashboard') {
        setReportData(res.data);
      } else {
        const isGroupedReport = config?.key === 'costByEventTypeAndConcept' || config?.key === 'spendByLine';
        setReportData(isGroupedReport && res.data?.vehicles ? res.data : (Array.isArray(res.data) ? res.data : []));
      }
      setMeta(res.meta || null);
    } else {
      setToast({ isOpen: true, message: res.message || 'Error al cargar el reporte', type: 'error' });
    }
    setLoading(false);
  };

  const exportToExcel = () => {
    const isGroupedObj = (reportType === 'cost-by-event-type-and-concept' || reportType === 'spend-by-line') && reportData?.vehicles;
    const hasData = isGroupedObj
      ? (reportData.vehicles?.length > 0 || reportData.machines?.length > 0)
      : (Array.isArray(reportData) && reportData.length > 0);
    if (!hasData) {
      setToast({ isOpen: true, message: 'No hay datos para exportar', type: 'warning' });
      return;
    }
    const config = REPORT_TYPES.find((r) => r.value === reportType);
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet('Reporte', { views: [{ state: 'frozen', ySplit: 2, activeCell: 'A3' }] });

    const borderThin = {
      top: { style: 'thin', color: { argb: 'FFD1D5DB' } },
      left: { style: 'thin', color: { argb: 'FFD1D5DB' } },
      bottom: { style: 'thin', color: { argb: 'FFD1D5DB' } },
      right: { style: 'thin', color: { argb: 'FFD1D5DB' } },
    };
    const titleStyle = {
      font: { bold: true, size: 14, color: { argb: 'FF1F2937' } },
      alignment: { vertical: 'middle' },
    };
    const subtitleStyle = {
      font: { size: 11, color: { argb: 'FF6B7280' } },
      alignment: { vertical: 'middle' },
    };
    const headerStyle = {
      font: { bold: true, size: 11, color: { argb: 'FFFFFFFF' } },
      fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0F766E' } },
      alignment: { horizontal: 'center', vertical: 'middle', wrapText: true },
      border: borderThin,
    };
    const cellTextLeft = { alignment: { horizontal: 'left', vertical: 'middle' }, border: borderThin };
    const cellNumberRight = { alignment: { horizontal: 'right', vertical: 'middle' }, border: borderThin };

    // Fila de título y período
    ws.addRow([config?.label || 'Reporte de Maquinaria']);
    ws.getRow(1).height = 24;
    ws.getCell(1, 1).style = titleStyle;
    ws.mergeCells(1, 1, 1, 18);
    ws.addRow([`Período: ${dateFrom} a ${dateTo}`]);
    ws.getRow(2).height = 20;
    ws.getCell(2, 1).style = subtitleStyle;
    ws.mergeCells(2, 1, 2, 18);

    if (reportType === 'spend-by-vehicle') {
      const headers = [
        'Código', 'Vehículo', 'Gasto total (sin IGV)', 'Tiempo período (h)', 'Δ horómetro (h)', 'Km recorridos', 'Eventos',
      ];
      const widths = [14, 26, 14, 14, 14, 14, 10];
      const row = ws.addRow(headers);
      row.height = 22;
      row.eachCell((c, colNumber) => { c.style = { ...headerStyle }; ws.getColumn(colNumber).width = widths[colNumber - 1] || 12; });
      reportData.forEach((r) => {
        const dataRow = ws.addRow([
          r.vehicle_code || '',
          formatVehicleNameWithPlate(r),
          formatCurrency(r.total_spend),
          formatNumber(r.total_hours),
          r.total_horometro != null ? formatNumber(r.total_horometro, 2) : '—',
          r.total_kilometers != null ? formatNumber(r.total_kilometers, 2) : '—',
          r.event_count != null ? formatNumber(r.event_count, 0) : '—',
        ]);
        dataRow.eachCell((cell, colNumber) => {
          cell.style = colNumber <= 2 ? cellTextLeft : cellNumberRight;
        });
      });
    } else if (reportType === 'spend-by-machine') {
      const headers = [
        'Código', 'Maquinaria', 'Gasto total (sin IGV)', 'Tiempo período (h)', 'Δ horómetro (h)', 'Km recorridos', 'Eventos',
      ];
      const widths = [14, 26, 14, 14, 14, 14, 10];
      const row = ws.addRow(headers);
      row.height = 22;
      row.eachCell((c, colNumber) => { c.style = { ...headerStyle }; ws.getColumn(colNumber).width = widths[colNumber - 1] || 12; });
      reportData.forEach((r) => {
        const dataRow = ws.addRow([
          r.machine_code || '',
          r.machine_name || '',
          formatCurrency(r.total_spend),
          formatNumber(r.total_hours),
          r.total_horometro != null ? formatNumber(r.total_horometro, 2) : '—',
          r.total_kilometers != null ? formatNumber(r.total_kilometers, 2) : '—',
          r.event_count != null ? formatNumber(r.event_count, 0) : '—',
        ]);
        dataRow.eachCell((cell, colNumber) => {
          cell.style = colNumber <= 2 ? cellTextLeft : cellNumberRight;
        });
      });
    } else if (reportType === 'cost-by-event-type-and-concept') {
      const headers = ['Bloque', 'Unidad', 'Tipo de evento', 'Producto / Servicio', 'Tipo', 'Gasto total (sin IGV)', 'Cantidades', 'Horas período', 'Δ horóm. (h)', 'Costo/hora', 'Cant./hora', 'Eventos'];
      const widths = [12, 20, 20, 28, 10, 14, 12, 11, 11, 12, 12, 10];
      const row = ws.addRow(headers);
      row.height = 22;
      row.eachCell((c, colNumber) => { c.style = { ...headerStyle }; ws.getColumn(colNumber).width = widths[colNumber - 1] || 12; });
      const addRows = (bloque, unidad, list) => {
        (list || []).forEach((item) => {
          (item.event_types || []).forEach((et) => {
            (et.concepts || []).forEach((c) => {
              const dataRow = ws.addRow([
                bloque,
                unidad,
                et.event_type_name || '—',
                c.concept_name || '—',
                c.concept_type === 'product' ? 'Producto' : c.concept_type === 'service' ? 'Servicio' : '—',
                formatCurrency(c.total_spend),
                c.concept_type === 'product' && c.total_quantity != null ? formatNumber(c.total_quantity) : '—',
                formatNumber(c.total_hours),
                c.total_horometro != null ? formatNumber(c.total_horometro, 2) : '—',
                c.cost_per_hour != null ? formatCurrency(c.cost_per_hour) : '—',
                c.concept_type === 'product' && c.quantity_per_hour != null ? formatNumber(c.quantity_per_hour) : '—',
                c.event_count != null ? formatNumber(c.event_count, 0) : '—',
              ]);
              dataRow.eachCell((cell, colNumber) => {
                cell.style = colNumber <= 5 ? cellTextLeft : cellNumberRight;
              });
            });
          });
        });
      };
      (reportData.vehicles || []).forEach((v) => addRows('Vehículo', v.vehicle_label || '—', [v]));
      (reportData.machines || []).forEach((m) => addRows('Maquinaria', m.machine_label || '—', [m]));
    } else if (reportType === 'spend-by-line') {
      const headers = ['Tipo de activo', 'Línea', 'Gasto total (sin IGV)', 'Horas período', 'Costo/hora', 'Eventos'];
      const widths = [16, 26, 16, 14, 14, 10];
      const row = ws.addRow(headers);
      row.height = 22;
      row.eachCell((c, colNumber) => { c.style = { ...headerStyle }; ws.getColumn(colNumber).width = widths[colNumber - 1] || 12; });
      (reportData.vehicles || []).forEach((v) => {
        const dataRow = ws.addRow([
          'Vehículo',
          v.line_name || 'Otro',
          formatCurrency(v.total_spend),
          formatNumber(v.total_hours),
          v.cost_per_hour != null ? formatCurrency(v.cost_per_hour) : '—',
          v.event_count != null ? formatNumber(v.event_count, 0) : '—',
        ]);
        dataRow.eachCell((cell, colNumber) => {
          cell.style = colNumber <= 2 ? cellTextLeft : cellNumberRight;
        });
      });
      (reportData.machines || []).forEach((m) => {
        const dataRow = ws.addRow([
          'Maquinaria',
          m.line_name || 'Otro',
          formatCurrency(m.total_spend),
          formatNumber(m.total_hours),
          m.cost_per_hour != null ? formatCurrency(m.cost_per_hour) : '—',
          m.event_count != null ? formatNumber(m.event_count, 0) : '—',
        ]);
        dataRow.eachCell((cell, colNumber) => {
          cell.style = colNumber <= 2 ? cellTextLeft : cellNumberRight;
        });
      });
    } else if (reportType === 'monetary-inconsistencies') {
      const headers = ['ID evento', 'Fecha evento', 'Hora', 'Tipo evento', 'Activo', 'Proyecto evento', 'Origen', 'Fecha origen', 'Proyecto origen', 'Inconsistencia'];
      const widths = [10, 12, 8, 20, 24, 24, 44, 12, 24, 16];
      const row = ws.addRow(headers);
      row.height = 22;
      row.eachCell((c, colNumber) => { c.style = { ...headerStyle }; ws.getColumn(colNumber).width = widths[colNumber - 1] || 12; });
      (reportData || []).forEach((r) => {
        const activo = r.vehicle || r.machine || '—';
        const origen = formatInconsistencySource(r);
        const incons = r.inconsistency_type === 'fecha_y_proyecto'
          ? 'Fecha y proyecto'
          : (r.inconsistency_type === 'fecha' ? 'Fecha' : 'Proyecto');
        const dataRow = ws.addRow([
          r.event_id,
          r.event_date || '—',
          r.event_time || '—',
          r.event_type || '—',
          activo,
          r.event_project_name || '—',
          origen,
          r.source_date || '—',
          r.source_project_name || '—',
          incons,
        ]);
        dataRow.eachCell((cell, colNumber) => {
          cell.style = colNumber <= 6 ? cellTextLeft : (colNumber <= 10 ? cellTextLeft : cellNumberRight);
        });
        if (r.source_href) {
          const oCell = dataRow.getCell(7);
          const base = typeof window !== 'undefined' ? window.location.origin : '';
          oCell.value = { text: origen, hyperlink: base + r.source_href };
          oCell.font = { color: { argb: 'FF0F766E' }, underline: true };
        }
      });
    }

    wb.xlsx.writeBuffer().then((buffer) => {
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Maquinaria_${config.label.replace(/\s+/g, '_')}_${dateFrom}_${dateTo}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      setToast({ isOpen: true, message: 'Exportado correctamente', type: 'success' });
    }).catch(() => {
      setToast({ isOpen: true, message: 'Error al exportar', type: 'error' });
    });
  };

  const showVehicleFilter = ['spend-by-vehicle', 'cost-by-event-type-and-concept', 'monetary-inconsistencies'].includes(reportType);
  const showMachineFilter = ['spend-by-machine', 'monetary-inconsistencies'].includes(reportType);
  const showProjectFilter = true;
  const isDashboard = reportType === 'dashboard';
  const hasDashboardData = isDashboard && (reportData?.kpis || reportData?.usage_kpis);
  const groupedVehiclesByLine = (reportData?.vehicles || []).reduce((acc, row) => {
    const vehicle = vehiclesById[String(row.vehicle_id)] || null;
    const line = lineLabelForAsset(vehicle, 'vehicle');
    if (!acc[line]) acc[line] = [];
    acc[line].push(row);
    return acc;
  }, {});
  const groupedMachinesByLine = (reportData?.machines || []).reduce((acc, row) => {
    const machine = machinesById[String(row.machine_id)] || null;
    const line = lineLabelForAsset(machine, 'machine');
    if (!acc[line]) acc[line] = [];
    acc[line].push(row);
    return acc;
  }, {});

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-800">Reportes de Maquinaria</h1>
          <p className="text-gray-600 mt-1">Dashboard con indicadores, gráficos y reportes detallados de la flota</p>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de reporte</label>
              <select
                value={reportType}
                onChange={(e) => {
                  setReportType(e.target.value);
                  invalidateReport();
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              >
                {REPORT_TYPES.map((r) => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Desde</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => {
                  setDateFrom(e.target.value);
                  invalidateReport();
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Hasta</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => {
                  setDateTo(e.target.value);
                  invalidateReport();
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              />
            </div>
            {showVehicleFilter && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Vehículo</label>
                <select
                  value={vehicleId}
                  onChange={(e) => {
                    setVehicleId(e.target.value);
                    invalidateReport();
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                >
                  <option value="">Todos</option>
                  {vehicles.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.code} – {v.name}{v.license_plate ? ` · ${v.license_plate}` : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {showMachineFilter && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Maquinaria</label>
                <select
                  value={machineId}
                  onChange={(e) => {
                    setMachineId(e.target.value);
                    invalidateReport();
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                >
                  <option value="">Todas</option>
                  {machines.map((m) => (
                    <option key={m.id} value={m.id}>{m.code} – {m.name}</option>
                  ))}
                </select>
              </div>
            )}
            {showProjectFilter && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Proyecto</label>
                <select
                  value={projectId}
                  onChange={(e) => {
                    setProjectId(e.target.value);
                    invalidateReport();
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                >
                  <option value="">Todos</option>
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>{p.code || ''} – {p.name}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
          <div className="mt-4 flex gap-2">
            <button
              type="button"
              onClick={loadReport}
              disabled={loading}
              className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 font-medium"
            >
              {loading ? 'Cargando…' : 'Generar reporte'}
            </button>
            {(((reportType === 'cost-by-event-type-and-concept' || reportType === 'spend-by-line')
              && reportData?.vehicles && (reportData.vehicles.length > 0 || reportData.machines?.length > 0))
              || (Array.isArray(reportData) && reportData.length > 0)) && !isDashboard && (
              <button
                type="button"
                onClick={exportToExcel}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 font-medium"
              >
                Exportar Excel
              </button>
            )}
          </div>
        </div>

        {meta && !isDashboard && (
          <p className="text-sm text-gray-500 mb-2">
            Período: {meta.date_from} a {meta.date_to}
          </p>
        )}

        <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
          {isDashboard && hasDashboardData && !loading && (
            <MachineryReportsDashboard
              data={reportData}
              maintenanceAlerts={maintenanceAlerts}
              meta={meta}
            />
          )}
          {(() => {
            const hasGrouped = reportData?.vehicles && (reportData.vehicles.length > 0 || reportData.machines?.length > 0);
            const hasFlat = Array.isArray(reportData) && reportData.length > 0;
            if (loading) return false;
            if (isDashboard && hasDashboardData) return false;
            if (hasGrouped || hasFlat) return false;
            return true;
          })() && (
            <div className="p-12 text-center text-gray-500">
              {reportType === 'dashboard'
                ? 'Seleccione el rango de fechas y pulse «Generar reporte» para ver el dashboard.'
                : reportType === 'cost-by-event-type-and-concept' && reportData?.vehicles
                ? 'No hay datos para el período y filtros seleccionados. Ajuste fechas, vehículo o proyecto y vuelva a generar.'
                : ['spend-by-vehicle', 'spend-by-machine'].includes(reportType) && meta
                  ? 'No hay datos para el período y filtros seleccionados. Ajuste fechas, unidad o proyecto y vuelva a generar.'
                  : 'Seleccione tipo de reporte y rango de fechas, luego pulse «Generar reporte».'}
            </div>
          )}
          {loading && (
            <div className="p-12 text-center text-gray-500">Cargando…</div>
          )}
          {reportData?.vehicles && (reportData.vehicles.length > 0 || reportData.machines?.length > 0) && !loading && reportType === 'cost-by-event-type-and-concept' && (
            <div className="p-4 space-y-6">
              {/* Bloque Vehículos (solo vehículos, sin maquinaria dentro) */}
              {reportData.vehicles?.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-slate-800 mb-3 pb-2 border-b border-slate-200">Vehículos</h3>
                  <div className="space-y-5">
                    {Object.entries(groupedVehiclesByLine).map(([lineName, vehiclesInLine]) => (
                      <div key={`line-v-${lineName}`} className="space-y-3">
                        <h4 className="text-sm font-semibold text-teal-700">Línea: {lineName} ({vehiclesInLine.length})</h4>
                        {vehiclesInLine.map((vehicle, vIdx) => {
                      const vKey = `v-${lineName}-${vIdx}`;
                      const vOpen = isSectionOpen(vKey);
                      const eventTypes = Array.isArray(vehicle.event_types) ? vehicle.event_types : [];
                      return (
                        <div key={vKey} className="border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm">
                          <button
                            type="button"
                            onClick={() => toggleSection(vKey)}
                            className="w-full px-4 py-3 flex items-center justify-between text-left bg-slate-100 hover:bg-slate-200 transition-colors"
                          >
                            <span className="font-semibold text-slate-800 flex items-center gap-2">
                              <span className="text-slate-500">{vOpen ? '▼' : '▶'}</span>
                              {vehicle.vehicle_label || 'Sin vehículo'}
                            </span>
                          </button>
                          {vOpen && (
                            <div className="border-t border-gray-200 bg-slate-50/50 p-4">
                              {eventTypes.map((et, eIdx) => {
                                const eKey = `${vKey}-e-${eIdx}`;
                                const eOpen = isSectionOpen(eKey);
                                return (
                                  <div key={eKey} className="mt-3 first:mt-0 border border-gray-100 rounded-lg overflow-hidden bg-white">
                                    <button
                                      type="button"
                                      onClick={() => toggleSection(eKey)}
                                      className="w-full px-4 py-2 flex items-center justify-between text-left bg-amber-50 hover:bg-amber-100/80 transition-colors border-b border-amber-100"
                                    >
                                      <span className="text-sm font-medium text-amber-900 flex items-center gap-2">
                                        <span className="text-amber-600">{eOpen ? '▼' : '▶'}</span>
                                        Evento: {et.event_type_name || '—'}
                                      </span>
                                    </button>
                                    {eOpen && (() => {
                                        const concepts = et.concepts || [];
                                        const showQuantityCols = concepts.some((c) => c.concept_type === 'product');
                                        return (
                                          <div className="overflow-x-auto">
                                            <table className="min-w-full text-sm">
                                              <thead className="bg-gray-50">
                                                <tr>
                                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">Producto / Servicio</th>
                                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">Tipo</th>
                                                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-600 uppercase">Gasto total (sin IGV)</th>
                                                  {showQuantityCols && <th className="px-4 py-2 text-right text-xs font-medium text-gray-600 uppercase">Cantidades</th>}
                                                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-600 uppercase" title="Tiempo entre primer y último evento (calendario)">Horas período</th>
                                                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-600 uppercase" title="Última − primera lectura de horómetro en el período">Δ horóm.</th>
                                                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-600 uppercase" title="Gasto ÷ horas: usa Δ horómetro si &gt; 0; si no, horas período">Costo/hora</th>
                                                  {showQuantityCols && <th className="px-4 py-2 text-right text-xs font-medium text-gray-600 uppercase" title="Cantidad ÷ horas: usa Δ horómetro si &gt; 0; si no, horas período">Cant./hora</th>}
                                                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-600 uppercase">Eventos</th>
                                                </tr>
                                              </thead>
                                              <tbody className="divide-y divide-gray-100">
                                                {concepts.map((c, cIdx) => (
                                                  <tr key={cIdx} className="hover:bg-gray-50/50">
                                                    <td className="px-4 py-2 text-gray-900">{c.concept_name || '—'}</td>
                                                    <td className="px-4 py-2 text-gray-600">{c.concept_type === 'product' ? 'Producto' : c.concept_type === 'service' ? 'Servicio' : '—'}</td>
                                                    <td className="px-4 py-2 text-right font-medium text-gray-900">{formatCurrency(c.total_spend)}</td>
                                                    {showQuantityCols && <td className="px-4 py-2 text-right">{c.concept_type === 'product' ? formatNumber(c.total_quantity) : '—'}</td>}
                                                    <td className="px-3 py-2 text-right">{formatNumber(c.total_hours)}</td>
                                                    <td className="px-3 py-2 text-right">{c.total_horometro != null ? formatNumber(c.total_horometro, 2) : '—'}</td>
                                                    <td className="px-4 py-2 text-right">{c.cost_per_hour != null ? formatCurrency(c.cost_per_hour) : '—'}</td>
                                                    {showQuantityCols && <td className="px-4 py-2 text-right">{c.concept_type === 'product' ? (c.quantity_per_hour != null ? formatNumber(c.quantity_per_hour) : '—') : '—'}</td>}
                                                    <td className="px-4 py-2 text-right">{c.event_count}</td>
                                                  </tr>
                                                ))}
                                              </tbody>
                                            </table>
                                          </div>
                                        );
                                      })()}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                        })}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {/* Bloque Maquinarias (solo maquinarias, sin vehículo dentro) */}
              {reportData.machines?.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-slate-800 mb-3 pb-2 border-b border-slate-200">Maquinarias</h3>
                  <div className="space-y-5">
                    {Object.entries(groupedMachinesByLine).map(([lineName, machinesInLine]) => (
                      <div key={`line-m-${lineName}`} className="space-y-3">
                        <h4 className="text-sm font-semibold text-amber-700">Línea: {lineName} ({machinesInLine.length})</h4>
                        {machinesInLine.map((machine, mIdx) => {
                      const mKey = `m-${lineName}-${mIdx}`;
                      const mOpen = isSectionOpen(mKey);
                      const eventTypes = Array.isArray(machine.event_types) ? machine.event_types : [];
                      return (
                        <div key={mKey} className="border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm">
                          <button
                            type="button"
                            onClick={() => toggleSection(mKey)}
                            className="w-full px-4 py-3 flex items-center justify-between text-left bg-blue-50 hover:bg-blue-100 transition-colors"
                          >
                            <span className="font-semibold text-slate-800 flex items-center gap-2">
                              <span className="text-blue-600">{mOpen ? '▼' : '▶'}</span>
                              {machine.machine_label || 'Sin maquinaria'}
                            </span>
                          </button>
                          {mOpen && (
                            <div className="border-t border-gray-200 bg-blue-50/30 p-4">
                              {eventTypes.map((et, eIdx) => {
                                const eKey = `${mKey}-e-${eIdx}`;
                                const eOpen = isSectionOpen(eKey);
                                return (
                                  <div key={eKey} className="mt-3 first:mt-0 border border-gray-100 rounded-lg overflow-hidden bg-white">
                                    <button
                                      type="button"
                                      onClick={() => toggleSection(eKey)}
                                      className="w-full px-4 py-2 flex items-center justify-between text-left bg-amber-50 hover:bg-amber-100/80 transition-colors border-b border-amber-100"
                                    >
                                      <span className="text-sm font-medium text-amber-900 flex items-center gap-2">
                                        <span className="text-amber-600">{eOpen ? '▼' : '▶'}</span>
                                        Evento: {et.event_type_name || '—'}
                                      </span>
                                    </button>
                                    {eOpen && (() => {
                                        const concepts = et.concepts || [];
                                        const showQuantityCols = concepts.some((c) => c.concept_type === 'product');
                                        return (
                                          <div className="overflow-x-auto">
                                            <table className="min-w-full text-sm">
                                              <thead className="bg-gray-50">
                                                <tr>
                                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">Producto / Servicio</th>
                                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">Tipo</th>
                                                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-600 uppercase">Gasto total (sin IGV)</th>
                                                  {showQuantityCols && <th className="px-4 py-2 text-right text-xs font-medium text-gray-600 uppercase">Cantidades</th>}
                                                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-600 uppercase" title="Tiempo entre primer y último evento (calendario)">Horas período</th>
                                                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-600 uppercase" title="Última − primera lectura de horómetro en el período">Δ horóm.</th>
                                                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-600 uppercase" title="Gasto ÷ horas: usa Δ horómetro si &gt; 0; si no, horas período">Costo/hora</th>
                                                  {showQuantityCols && <th className="px-4 py-2 text-right text-xs font-medium text-gray-600 uppercase" title="Cantidad ÷ horas: usa Δ horómetro si &gt; 0; si no, horas período">Cant./hora</th>}
                                                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-600 uppercase">Eventos</th>
                                                </tr>
                                              </thead>
                                              <tbody className="divide-y divide-gray-100">
                                                {concepts.map((c, cIdx) => (
                                                  <tr key={cIdx} className="hover:bg-gray-50/50">
                                                    <td className="px-4 py-2 text-gray-900">{c.concept_name || '—'}</td>
                                                    <td className="px-4 py-2 text-gray-600">{c.concept_type === 'product' ? 'Producto' : c.concept_type === 'service' ? 'Servicio' : '—'}</td>
                                                    <td className="px-4 py-2 text-right font-medium text-gray-900">{formatCurrency(c.total_spend)}</td>
                                                    {showQuantityCols && <td className="px-4 py-2 text-right">{c.concept_type === 'product' ? formatNumber(c.total_quantity) : '—'}</td>}
                                                    <td className="px-3 py-2 text-right">{formatNumber(c.total_hours)}</td>
                                                    <td className="px-3 py-2 text-right">{c.total_horometro != null ? formatNumber(c.total_horometro, 2) : '—'}</td>
                                                    <td className="px-4 py-2 text-right">{c.cost_per_hour != null ? formatCurrency(c.cost_per_hour) : '—'}</td>
                                                    {showQuantityCols && <td className="px-4 py-2 text-right">{c.concept_type === 'product' ? (c.quantity_per_hour != null ? formatNumber(c.quantity_per_hour) : '—') : '—'}</td>}
                                                    <td className="px-4 py-2 text-right">{c.event_count}</td>
                                                  </tr>
                                                ))}
                                              </tbody>
                                            </table>
                                          </div>
                                        );
                                      })()}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                        })}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          {reportData?.vehicles && (reportData.vehicles.length > 0 || reportData.machines?.length > 0) && !loading && reportType === 'spend-by-line' && (
            <div className="p-4 space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-slate-800 mb-3 pb-2 border-b border-slate-200">Vehículos</h3>
                {reportData.vehicles?.length ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">Línea</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-600 uppercase">Gasto total (sin IGV)</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-600 uppercase">Horas período</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-600 uppercase">Costo/hora</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-600 uppercase">Eventos</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {reportData.vehicles.map((row, idx) => (
                          <tr key={`sl-v-${idx}`} className="hover:bg-gray-50/50">
                            <td className="px-4 py-2 text-gray-900">{row.line_name || 'Otro'}</td>
                            <td className="px-4 py-2 text-right font-medium">{formatCurrency(row.total_spend)}</td>
                            <td className="px-4 py-2 text-right">{formatNumber(row.total_hours)}</td>
                            <td className="px-4 py-2 text-right">{row.cost_per_hour != null ? formatCurrency(row.cost_per_hour) : '—'}</td>
                            <td className="px-4 py-2 text-right">{row.event_count}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-sm text-gray-500">Sin datos para vehículos.</div>
                )}
              </div>

              <div>
                <h3 className="text-lg font-semibold text-slate-800 mb-3 pb-2 border-b border-slate-200">Maquinarias</h3>
                {reportData.machines?.length ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">Línea</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-600 uppercase">Gasto total (sin IGV)</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-600 uppercase">Horas período</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-600 uppercase">Costo/hora</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-600 uppercase">Eventos</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {reportData.machines.map((row, idx) => (
                          <tr key={`sl-m-${idx}`} className="hover:bg-gray-50/50">
                            <td className="px-4 py-2 text-gray-900">{row.line_name || 'Otro'}</td>
                            <td className="px-4 py-2 text-right font-medium">{formatCurrency(row.total_spend)}</td>
                            <td className="px-4 py-2 text-right">{formatNumber(row.total_hours)}</td>
                            <td className="px-4 py-2 text-right">{row.cost_per_hour != null ? formatCurrency(row.cost_per_hour) : '—'}</td>
                            <td className="px-4 py-2 text-right">{row.event_count}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-sm text-gray-500">Sin datos para maquinarias.</div>
                )}
              </div>
            </div>
          )}
          {Array.isArray(reportData) && reportData.length > 0 && !loading && reportType !== 'cost-by-event-type-and-concept' && !isDashboard && (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {reportType === 'spend-by-vehicle' && (
                      <>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Código</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Vehículo</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-700 uppercase">Gasto total (sin IGV)</th>
                        <th className="px-3 py-3 text-right text-xs font-medium text-gray-700 uppercase" title="Entre primer y último evento (calendario)">Tiempo período (h)</th>
                        <th className="px-3 py-3 text-right text-xs font-medium text-gray-700 uppercase" title="Última lectura de horómetro − primera en el período">Δ horómetro (h)</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-700 uppercase">Km recorridos</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-700 uppercase">Eventos</th>
                      </>
                    )}
                    {reportType === 'spend-by-machine' && (
                      <>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Código</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Maquinaria</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-700 uppercase">Gasto total (sin IGV)</th>
                        <th className="px-3 py-3 text-right text-xs font-medium text-gray-700 uppercase" title="Entre primer y último evento (calendario)">Tiempo período (h)</th>
                        <th className="px-3 py-3 text-right text-xs font-medium text-gray-700 uppercase" title="Última lectura de horómetro − primera en el período">Δ horómetro (h)</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-700 uppercase">Km recorridos</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-700 uppercase">Eventos</th>
                      </>
                    )}
                    {reportType === 'monetary-inconsistencies' && (
                      <>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-700 uppercase">ID evento</th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-700 uppercase">Fecha evento</th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-700 uppercase">Hora</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Tipo evento</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Activo</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Proyecto evento</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Origen</th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-700 uppercase">Fecha origen</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Proyecto origen</th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-700 uppercase">Inconsistencia</th>
                      </>
                    )}
                    </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {reportType === 'spend-by-vehicle' && reportData.map((r, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900">{r.vehicle_code}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{formatVehicleNameWithPlate(r)}</td>
                      <td className="px-4 py-3 text-sm text-right font-medium">{formatCurrency(r.total_spend)}</td>
                      <td className="px-3 py-3 text-sm text-right text-gray-700">{formatNumber(r.total_hours)}</td>
                      <td className="px-3 py-3 text-sm text-right">{r.total_horometro != null ? formatNumber(r.total_horometro, 2) : '—'}</td>
                      <td className="px-4 py-3 text-sm text-right">{r.total_kilometers != null ? formatNumber(r.total_kilometers, 2) : '—'}</td>
                      <td className="px-4 py-3 text-sm text-right">{r.event_count}</td>
                    </tr>
                  ))}
                  {reportType === 'spend-by-machine' && reportData.map((r, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900">{r.machine_code}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{r.machine_name}</td>
                      <td className="px-4 py-3 text-sm text-right font-medium">{formatCurrency(r.total_spend)}</td>
                      <td className="px-3 py-3 text-sm text-right text-gray-700">{formatNumber(r.total_hours)}</td>
                      <td className="px-3 py-3 text-sm text-right">{r.total_horometro != null ? formatNumber(r.total_horometro, 2) : '—'}</td>
                      <td className="px-4 py-3 text-sm text-right">{r.total_kilometers != null ? formatNumber(r.total_kilometers, 2) : '—'}</td>
                      <td className="px-4 py-3 text-sm text-right">{r.event_count}</td>
                    </tr>
                  ))}
                  {reportType === 'monetary-inconsistencies' && reportData.map((r, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-3 py-3 text-sm text-gray-900">{r.event_id}</td>
                      <td className="px-3 py-3 text-sm text-gray-900">{r.event_date || '—'}</td>
                      <td className="px-3 py-3 text-sm text-gray-700">{r.event_time || '—'}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{r.event_type || '—'}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{r.vehicle || r.machine || '—'}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{r.event_project_name || '—'}</td>
                      <td className="px-4 py-3 text-sm text-gray-900 max-w-md">
                        {r.source_href ? (
                          <Link
                            to={r.source_href}
                            className="text-teal-700 hover:text-teal-900 hover:underline font-medium"
                            title="Abrir documento o guía"
                          >
                            {formatInconsistencySource(r)}
                          </Link>
                        ) : (
                          <span title={formatInconsistencySource(r)}>{formatInconsistencySource(r)}</span>
                        )}
                      </td>
                      <td className="px-3 py-3 text-sm text-gray-900">{r.source_date || '—'}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{r.source_project_name || '—'}</td>
                      <td className="px-3 py-3 text-sm text-gray-900">
                        {r.inconsistency_type === 'fecha_y_proyecto'
                          ? 'Fecha y proyecto'
                          : (r.inconsistency_type === 'fecha' ? 'Fecha' : 'Proyecto')}
                      </td>
                    </tr>
                  ))}
                  </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      <Toast isOpen={toast.isOpen} message={toast.message} type={toast.type} onClose={() => setToast((t) => ({ ...t, isOpen: false }))} />
    </div>
  );
}
